/* eslint-disable max-lines-per-function */
import React from "react";
import { isEqual } from "lodash";

import { Button } from "components/haven-design";
import { useSnackbar } from "components/haven-design/snackbar";
import { emptyDelta } from "components/message-editor";
import { Delta } from "components/message-editor/types";
import { quillDeltaToHtml, quillDeltaToPlaintext } from "components/message-editor/utils";
import { TemplateType } from "types/gql-types";
import { DialogActions } from "components/haven-design/modal/dialog";
import { Props } from ".";


type ValueHandlers = {
  smsValue: Delta
  setSmsValue: React.Dispatch<React.SetStateAction<Delta>>
  letterValue: Delta
  setLetterValue: React.Dispatch<React.SetStateAction<Delta>>
  emailValue: Delta
  setEmailValue: React.Dispatch<React.SetStateAction<Delta>>
  emailSubject: string
  setEmailSubject: React.Dispatch<React.SetStateAction<string>>
  emailBccAddress: string
  setEmailBccAddress: React.Dispatch<React.SetStateAction<string>>
  smsEnabled: boolean
  setSmsEnabled: React.Dispatch<React.SetStateAction<boolean>>
  emailEnabled: boolean
  setEmailEnabled: React.Dispatch<React.SetStateAction<boolean>>
  letterEnabled: boolean
  defaultEmailEnabled: boolean
  emailRespondToEnabled?: boolean
  smsPropertyHasUseCases: boolean
  smsPropertyUseCaseId: string | null
}

export const html = (editorInfo: Delta, type: string): string => {
  switch (type) {
    case TemplateType.SMS:
      return quillDeltaToPlaintext(editorInfo);
    default:
      return quillDeltaToHtml(editorInfo);
  }
};

const NO_CHANNEL_CONTENT_ERROR =
  "Please add a message for at least one channel (SMS, Email, or Letter).";
const NO_EMAIL_SUBJECT_ERROR = "Please add a Subject to your Email template.";
const NO_SMS_USE_CASE_ERROR = "Please specify a use case in order to send text messages.";

const SubmitHandler: React.FC<Props & ValueHandlers> = (props) => {
  const {
    smsValue,
    setSmsValue,
    emailValue,
    setEmailValue,
    emailSubject,
    setEmailSubject,
    emailBccAddress,
    setEmailBccAddress,
    smsEnabled,
    setSmsEnabled,
    emailEnabled,
    setEmailEnabled,
    letterValue,
    setLetterValue,
    letterEnabled,
    defaultEmailEnabled,
    smsPropertyUseCaseId,
    smsPropertyHasUseCases,
  } = props;

  const { openSnackbar } = useSnackbar();

  const resetForm = () => {
    setSmsValue(emptyDelta);
    setEmailValue(emptyDelta);
    setEmailSubject("");
    setEmailBccAddress("");
    setSmsEnabled(true);
    setEmailEnabled(true);
    setLetterValue(emptyDelta);
  };
  const handleSubmit = () => {
    if (
      (smsEnabled && isEqual(smsValue, emptyDelta)) ||
      (letterEnabled && isEqual(letterValue, emptyDelta)) ||
      (emailEnabled && isEqual(emailValue, emptyDelta))
    ) {
      openSnackbar({ message: NO_CHANNEL_CONTENT_ERROR, status: "error" });
      return;
    }

    if (!emailEnabled && !letterEnabled && !smsEnabled) {
      openSnackbar({ message: NO_CHANNEL_CONTENT_ERROR, status: "error" });
      return;
    }

    if (!isEqual(emailValue, emptyDelta) && emailSubject.length < 1 && emailEnabled) {
      openSnackbar({ message: NO_EMAIL_SUBJECT_ERROR, status: "error" });
      return;
    }

    if (smsEnabled && smsPropertyHasUseCases && smsPropertyUseCaseId === null) {
      openSnackbar({ message: NO_SMS_USE_CASE_ERROR, status: "error" });
      return;
    }

    // deferReset lets a caller with a confirm-before-send step (e.g. Select All) hold off
    // wiping the draft until the send is actually confirmed, not merely queued for confirmation.
    if (!props.deferReset) resetForm();

    if (props.handleSubmit) {
      props.handleSubmit({
        emailSubject,
        emailRespondToEnabled: defaultEmailEnabled,
        emailBccAddress: emailBccAddress.trim() || null,
        emailEnabled,
        smsEnabled,
        letterEnabled,
        emailBody: html(emailValue, TemplateType.EMAIL),
        smsBody: html(smsValue, TemplateType.SMS),
        letterBody: html(letterValue, TemplateType.LETTER),
        smsPropertyUseCaseId,
      }, resetForm);
    }
  };

  if (props.isHidingSubmitBtns) return null;
  const noChannelSelected = !(emailEnabled || smsEnabled || letterEnabled);

  return (
    <DialogActions>
      {props.onRequestClose && (
        <Button color="secondary" size="L"
          onClick={() => {
            resetForm();
            if (props.onRequestClose) props.onRequestClose();
          }}>
          Cancel
        </Button>
      )}
      <Button color="primary" size="L" disabled={props.isSending || noChannelSelected} onClick={handleSubmit}>
        Send Messages
      </Button>
    </DialogActions>
  );

};
export default SubmitHandler;
